import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { makeStyles } from '@mui/styles';
import PrintProvider, { Print, NoPrint } from 'react-easy-print';
import {
  Grid,
  Table,
  Dialog,
  Button,
  TableRow,
  TableBody,
  TableHead,
  Typography,
  DialogContent,
  CircularProgress,
  DialogTitle,
  Tooltip,
  IconButton,
  TableCell
} from '@mui/material';
import clsx from 'clsx';

import { callRestGetEndpoint } from '@/common/utils/api';
import { Purchase, Refund } from '../Types';
import { falsey } from '@/common/utils/appointments';
import { CloseIcon } from '@/common/icons/CloseIcon';
import { currencyFormat } from '../Refunds/utils';
import { EmailReceiptModal } from './EmailReceiptModal';

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: theme.shape.borderRadius,
    '& button': {
      fontWeight: 600
    }
  },
  gridBorderRight: {
    borderRight: `1px solid ${theme.palette.text.primary}`,
    height: '16px',
    marginLeft: '10px !important',
    marginRight: '10px !important'
  },
  gridPadding: {
    paddingTop: '24px'
  },
  dialogContent: {
    minHeight: 200,
    paddingTop: '10%'
  },
  dialogTitleContainer: {
    padding: '0px 24px 12px 24px'
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: 600,
    height: 42,
    paddingBottom: 0
  },
  topBtnContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    '& span': {
      lineHeight: '32px'
    }
  },
  startRefundBtn: {
    width: 126,
    height: 24,
    marginBottom: 8
  },
  table: {
    borderCollapse: 'collapse',
    '& thead tr': {
      border: 'none !important'
    },
    '& tbody tr': {
      border: 'none !important',
      borderBottom: '1px solid #D1D5DB !important'
    },
    '& tbody': {
      border: 'none !important'
    }
  },
  tableHeadingCell: {
    color: '#000 !important',
    padding: '8px !important',
    borderBottom: 'none !important',
    height: 14,
    lineHeight: '24px',
    backgroundColor: '#E6F0F9 !important',
    fontWeight: '600 !important',
    whiteSpace: 'nowrap'
  },
  tableCell: {
    color: '#000 !important',
    padding: '12px 8px !important',
    height: 18,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: '-webkit-baseline-middle',
    maxWidth: 200
  },
  summaryContainer: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  summary: {
    width: 460,
    display: 'flex',
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
    padding: 16,
    fontSize: 14
  },
  refundSummary: {
    padding: '16px 0 0 0',
    fontSize: 14
  },
  heading: {
    fontWeight: 600,
    fontSize: 14
  },
  subHeading: {
    fontWeight: 600,
    fontSize: 12
  },
  refundSubHeading: {
    fontWeight: 400,
    fontSize: 12
  },
  buttonContainer: {
    width: '100%',
    padding: '10px',
    display: 'flex',
    justifyContent: 'flex-end'
  }
}));

export type ReceiptsDetailsModalProps = {
  open: boolean;
  setModal: (o: boolean) => void;
  receiptId: number;
  customerName: string;
  customerEmail: string;
  setIsRefundInterfaceOpen: (o: boolean) => void;
};

const ReceiptsDetailsModal: React.FC<ReceiptsDetailsModalProps> = props => {
  const {
    open,
    setModal,
    receiptId,
    customerName,
    customerEmail,
    setIsRefundInterfaceOpen
  } = props;
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [purchase, setPurchase] = useState<Purchase | null>(null);
  const [emailReceiptModalOpen, setEmailReceiptModalOpen] = useState(false);

  // This useEffect is responsible for determining the PurchaseType, which
  // RefundModal needs in order to render differently for each type.
  // It also loads the ppmIds, amounts, toBalances, and maxRefunds arrays.
  // maxRefunds array is the maximum that the user can refund for a certain
  // PPM. ppmIds, amounts, toBalances are all used to hit the process_refund
  // endpoint, where ppmIds[i], amounts[i], toBalances[i] of each array is
  // looped over for each refund.
  useEffect(() => {
    if (receiptId) {
      (async () => {
        setLoading(true);
        const res = await callRestGetEndpoint(
          { purchase: receiptId },
          '/pos_refund/get_purchase_for_refund'
        );
        setPurchase(res?.data?.purchase);
        setLoading(false);
      })();
      setPurchase(null);
    }
  }, [receiptId]);

  const hasPaymentToRefund = purchase?.refund_version_available;

  return (
    <>
      <Dialog
        className={classes.root}
        open={open}
        onClose={(e, reason) => {
          if (reason && reason === 'backdropClick') {
            return;
          }
          setModal(false);
        }}
        maxWidth="md"
        fullWidth={true}
      >
        <DialogTitle className={classes.dialogTitleContainer}>
          <Grid container className={classes.dialogTitle}>
            View Receipt
            <Tooltip arrow title="Close" className="no-print">
              <IconButton
                onClick={() => {
                  setModal(!open);
                }}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Grid>
          <Grid item xs={12} mt={0}>
            <Grid
              container
              display="flex"
              justifyContent="left"
              alignItems="center"
            >
              <Grid item>
                <Typography variant="body4">
                  {moment(purchase?.purchasedate).format('MMMM Do, YYYY')}
                </Typography>
              </Grid>
              <Grid item className={classes.gridBorderRight}></Grid>
              <Grid item>
                <Typography variant="body4">{`Invoice ${receiptId}`}</Typography>
              </Grid>
              <Grid item className={classes.gridBorderRight}></Grid>
              <Grid item>
                <Typography variant="body4">{purchase?.store?.name}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <div className={classes.topBtnContainer}>
            <Typography variant="body3" color="textPrimary">
              Total Items: {purchase?.line_items?.length || 0}
            </Typography>
            <Button
              className={clsx(classes.startRefundBtn, 'no-print')}
              color="primary"
              variant="contained"
              disabled={purchase?.refund_version_available === 0 || loading}
              onClick={() => {
                setModal(!open);
                setIsRefundInterfaceOpen(true);
              }}
            >
              Start Refund
            </Button>
          </div>
          <PrintProvider loose>
            <Print name="receipt">
              {loading ? (
                <Grid
                  container
                  display="flex"
                  justifyContent={'center'}
                  className={classes.dialogContent}
                >
                  <CircularProgress />
                </Grid>
              ) : null}
              <PurchaseDetails purchase={purchase} />
            </Print>
          </PrintProvider>
        </DialogContent>
        <div className={classes.buttonContainer}>
          <NoPrint>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <Button
                  color="primary"
                  onClick={() => setEmailReceiptModalOpen(true)}
                >
                  Email Receipt
                </Button>
              </Grid>
              <Grid item>
                <Button color="primary" onClick={() => window.print()}>
                  Print Receipt
                </Button>
              </Grid>
              {(hasPaymentToRefund ?? 0) > 0 ? (
                <>
                  <Grid item>
                    <Button
                      color="primary"
                      variant="contained"
                      onClick={() => {
                        setModal(!open);
                      }}
                    >
                      Done
                    </Button>
                  </Grid>
                  <Grid item></Grid>
                </>
              ) : (
                <Grid item>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      setModal(!open);
                    }}
                  >
                    Done
                  </Button>
                </Grid>
              )}
            </Grid>
          </NoPrint>
        </div>
      </Dialog>
      {emailReceiptModalOpen && receiptId && (
        <EmailReceiptModal
          open={emailReceiptModalOpen}
          onClose={() => setEmailReceiptModalOpen(false)}
          receiptId={receiptId}
          customerEmail={customerEmail}
          customerName={customerName}
        />
      )}
    </>
  );
};

export default ReceiptsDetailsModal;

type PurchaseDetailsProps = {
  purchase?: Purchase;
};

const PurchaseDetails: React.FC<PurchaseDetailsProps> = ({ purchase }) => {
  const classes = useStyles();

  if (!purchase) {
    return null;
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Grid container display="flex" justifyContent="center">
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeadingCell}>Item</TableCell>
                <TableCell className={classes.tableHeadingCell}>
                  Price
                </TableCell>
                <TableCell className={classes.tableHeadingCell}>
                  Discount
                </TableCell>
                <TableCell className={classes.tableHeadingCell}>
                  Credits Used
                </TableCell>
                <TableCell className={classes.tableHeadingCell}>
                  Total
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchase?.line_items?.map(li => (
                <TableRow>
                  <TableCell className={classes.tableCell}>
                    {li?.description}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {currencyFormat(li?.list_price)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {currencyFormat(li?.manual_discount)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {li?.credits_used
                      ? `${li?.credits_used} ${li?.credit_description}`
                      : '---'}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {currencyFormat(li?.pre_tax_total)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
      </Grid>
      <Grid item xs={12} mt={2} className={classes.summaryContainer}>
        <Grid container className={classes.summary}>
          <Grid item xs={12}>
            Receipt
          </Grid>
          <Grid item xs={1} style={{ width: 24 }} />
          <Grid item xs={11}>
            <Grid container mt={2}>
              <Grid item xs={6} display="flex" justifyContent="flex-start">
                <span className={classes.subHeading}>Subtotal</span>
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <span className={classes.subHeading}>
                  {currencyFormat(purchase.subtotal)}
                </span>
              </Grid>
            </Grid>
            {purchase?.total_credits_used ? (
              <Grid container>
                <Grid item xs={6} display="flex" justifyContent="flex-start">
                  <span className={classes.subHeading}>Total Credits</span>
                </Grid>
                <Grid item xs={6} display="flex" justifyContent="flex-end">
                  <span className={classes.subHeading}>
                    {purchase?.total_credits_used}
                  </span>
                </Grid>
              </Grid>
            ) : null}
            {purchase?.promo_description ? (
              <Grid container>
                <Grid item xs={6} display="flex" justifyContent="flex-start">
                  <span className={classes.subHeading}>
                    {purchase?.promo_description}
                  </span>
                </Grid>
                <Grid item xs={6} display="flex" justifyContent="flex-end">
                  <span
                    className={classes.subHeading}
                  >{`-${currencyFormat(purchase?.promo_discount)}`}</span>
                </Grid>
              </Grid>
            ) : null}
            {
              <Grid container>
                <Grid item xs={6} display="flex" justifyContent="flex-start">
                  <span className={classes.subHeading}>Tax</span>
                </Grid>
                <Grid item xs={6} display="flex" justifyContent="flex-end">
                  <span className={classes.subHeading}>
                    {currencyFormat(purchase?.total_tax_applied || 0)}
                  </span>
                </Grid>
              </Grid>
            }
          </Grid>
          <Grid container xs={12}>
            {purchase?.prior_refunds?.length > 0 && (
              <Grid container className={classes.refundSummary}>
                <Grid item xs={12}>
                  Refunds
                </Grid>
                <Grid style={{ paddingBottom: '8px' }}>
                  {purchase?.prior_refunds?.map((rf: Refund) => (
                    <Grid container>
                      <Grid xs={1} style={{ width: 24 }} />
                      <Grid xs={11}>
                        <span className={classes.refundSubHeading}>
                          {moment(rf?.created_at).format('MM/DD/yyyy')} Refund
                          by {rf?.authorized_by}. {rf?.reason?.slice(0, 50)}
                        </span>
                        <span className={classes.refundSubHeading}>
                          {rf?.reason?.slice(50)}
                        </span>
                        <Grid
                          container
                          display="flex"
                          justifyContent={'flex-end'}
                        >
                          <Grid item xs={12}>
                            <Grid container>
                              <Grid
                                item
                                xs={6}
                                display="flex"
                                justifyContent="flex-start"
                              >
                                <span className={classes.subHeading}>
                                  {rf?.description}
                                </span>
                              </Grid>
                              <Grid
                                item
                                xs={6}
                                display="flex"
                                justifyContent="flex-end"
                              >
                                <span className={classes.subHeading}>
                                  {falsey(rf.credits_refunded)
                                    ? currencyFormat(rf.refunded)
                                    : rf.credits_refunded}
                                </span>
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
            <Grid container display="flex" justifyContent={'flex-end'}>
              <Grid item>
                {purchase.refund_unavailability_reason && (
                  <span className={classes.subHeading}>
                    {purchase.refund_unavailability_reason}
                  </span>
                )}
              </Grid>
            </Grid>
            <Grid
              container
              display="flex"
              justifyContent="flex-end"
              mt={2}
              mb={1}
              xs={12}
            >
              <Grid item xs={12} style={{ borderTop: '1px solid #D5D9DE' }} />
            </Grid>
            {purchase?.total ? (
              <Grid container mt={1}>
                <Grid item xs={6} display="flex" justifyContent="flex-start">
                  <span className={classes.heading}>Total Paid</span>
                </Grid>
                <Grid item xs={6} display="flex" justifyContent="flex-end">
                  <span className={classes.subHeading}>
                    {currencyFormat(purchase?.total)}
                  </span>
                </Grid>
              </Grid>
            ) : (
              <Grid container mt={3}>
                <Grid item xs={6} display="flex" justifyContent="flex-start">
                  <span className={classes.heading}>Total Paid</span>
                </Grid>
                <Grid item xs={6} display="flex" justifyContent="flex-end">
                  <span className={classes.subHeading}>
                    {currencyFormat(0.0)}
                  </span>
                </Grid>
              </Grid>
            )}
            {purchase?.payment_methods?.map(pm => (
              <Grid container>
                <Grid item xs={6} display="flex" justifyContent="flex-start">
                  <span className={classes.subHeading}>{pm?.description}</span>
                </Grid>
                <Grid item xs={6} display="flex" justifyContent="flex-end">
                  <span className={classes.subHeading}>
                    {currencyFormat(pm?.amount)}
                  </span>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
      <Print printOnly name="receipt">
        <Grid item xs={12} mt={3}>
          <Grid container display={'flex'} justifyContent="center">
            <Typography variant="body2" component={'p'}>
              Questions? Please call {purchase?.store?.phone} or email
              {' ' + purchase?.store?.email}
            </Typography>
            <Typography variant="body2" component={'p'}>
              Ready to book your next appointment? Visit www.restore.com
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={12} mt={1}>
          <Grid container display={'flex'} justifyContent="center">
            <Typography variant="body1">
              {purchase?.store?.address}, {purchase?.store?.store_code}
            </Typography>
          </Grid>
        </Grid>
      </Print>
    </Grid>
  );
};
